import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SheetClose } from '@/src/components/ui/sheet';
import styles from './A23CashGamesSort.module.css';
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from '@/src/components/ui/drawer';
import { VscFilter, VscFilterFilled } from "react-icons/vsc";
import { updateCashLobbyFilters, clearCashLobbyFilters, applyCashLobbyFiltersToRooms } from '@/Poker/redux/slices/lobbySlice';
import { SORT_BLINDS_LOW_HIGH } from '@/Poker/data/Constants';
import { IoCloseSharp } from 'react-icons/io5';
import useLobbySocket from '@/src/hooks/utils/useLobbySocket';
import { MdSort } from "react-icons/md";
import { MdFilterAlt, MdOutlineFilterAlt } from "react-icons/md";

export default function A23CashGamesSort() {
    const dispatch = useDispatch();
    const currentFilters = useSelector(state => state.lobby.cashLobbyFilterSortConfig);
    const { saveCashLobbySortPreferences } = useLobbySocket();
    const [isOpen, setIsOpen] = useState(false);

    // Local state for form controls
    const [filters, setFilters] = useState({
        favorites: false,
        sortBy: SORT_BLINDS_LOW_HIGH,
        stakes: {
            low: false,
            mid: false,
            high: false
        }
    });

    // Initialize local state from Redux on component mount
    useEffect(() => {
        // console.log('Spruha log >>> Mounting Cash Games Sort! ', currentFilters);
        setFilters({
            favorites: currentFilters.favorites,
            sortBy: currentFilters.sortBy,
            stakes: { ...currentFilters.stakes }
        });
    }, [currentFilters, isOpen]);

    // Handle favorites toggle
    const handleFavoritesChange = (checked) => {
        const newFilters = {
            ...filters,
            favorites: checked
        };
        setFilters(newFilters);
    };

    // Handle sort option change
    const handleSortChange = (sortValue) => {
        const newFilters = {
            ...filters,
            sortBy: sortValue
        };
        setFilters(newFilters);
    };

    // Handle stake checkbox changes
    const handleStakeChange = (stakeType, checked) => {
        const newFilters = {
            ...filters,
            stakes: {
                ...filters.stakes,
                [stakeType]: checked
            }
        };
        setFilters(newFilters);
    };

    // Handle apply filters
    const handleApplyFilters = () => {
        // Create the data object to send to Redux slice
        const filterData = {
            ...filters,
            // Add any additional computed properties
            hasActiveFilters: filters.favorites || Object.values(filters.stakes).some(stake => stake),
            appliedStakes: Object.entries(filters.stakes)
                .filter(([_, isSelected]) => isSelected)
                .map(([stakeType, _]) => stakeType)
        };

        saveCashLobbySortPreferences(filterData);

        // Update Redux store
        dispatch(updateCashLobbyFilters(filterData));
        // dispatch(applyCashLobbyFiltersToRooms({ filterConfig: filterData }));
    };

    // Handle clear filters
    const handleClearFilters = () => {
        const clearedFilters = {
            favorites: false,
            sortBy: SORT_BLINDS_LOW_HIGH,
            stakes: {
                low: false,
                mid: false,
                high: false
            },
            hasActiveFilters: false,
            appliedStakes: [],
            // Include table-level filters in the cleared state
            straddle: false,
            turbo: false,
            rit: false,
            anonymous: false,
            hideEmpty: false,
            hideFull: false
        };
        setFilters(clearedFilters);
        saveCashLobbySortPreferences(clearedFilters);
        dispatch(clearCashLobbyFilters());
    };

    return (
        <Drawer onOpenChange={(value) => setIsOpen(value)}>
            <DrawerTrigger className={styles.filterButtonContainer}>
                <div className={styles.filterButton}>
                    {
                        currentFilters.hasActiveFilters ?
                            <div className={styles.filterButtonRow}>
                                <MdFilterAlt size={20} color={'var(--color-success)'} />
                                <MdSort className={styles.sortIcon} size={10} color={'var(--color-success)'} />
                            </div>
                            :
                            <div className={styles.filterButtonRow}>
                                <MdOutlineFilterAlt size={20} color={'var(--color-secondary-light)'} />
                                <MdSort className={styles.sortIcon} size={10} color={'var(--color-secondary-light)'} />
                            </div>
                    }
                </div>
            </DrawerTrigger>
            <DrawerContent className={styles.drawerContent} side="bottom">
                <div className={styles['sort-filter-container']}>
                    <div className={styles.header}>
                        <h2 className={styles.title}>Sort & Filter</h2>
                        <SheetClose>
                            <IoCloseSharp className={styles.closeIcon} />
                        </SheetClose>
                    </div>
                    <div className={styles.content}>
                        {/* Favorites Section */}
                        <div className={styles['favorites-section']}>
                            <label className={`${styles['checkbox-label']} ${filters.favorites ? styles.selected : ''}`}>
                                <input
                                    type="checkbox"
                                    name="favorites"
                                    checked={filters.favorites}
                                    onChange={(e) => handleFavoritesChange(e.target.checked)}
                                />
                                <span>Show only favourite rooms</span>
                            </label>
                        </div>

                        {/* Sort Section */}
                        <div className={styles['sort-section']}>
                            <h3>Sort by:</h3>
                            <div className={styles['sort-options']}>
                                <label className={`${styles['radio-label']} ${filters.sortBy === SORT_BLINDS_LOW_HIGH ? styles.selected : ''}`}>
                                    <input
                                        type="radio"
                                        name="sort"
                                        value={SORT_BLINDS_LOW_HIGH}
                                        checked={filters.sortBy === SORT_BLINDS_LOW_HIGH}
                                        onChange={(e) => handleSortChange(e.target.value)}
                                    />
                                    <span>Blinds low to high</span>
                                </label>
                                <label className={`${styles['radio-label']} ${filters.sortBy === 'blinds-high-low' ? styles.selected : ''}`}>
                                    <input
                                        type="radio"
                                        name="sort"
                                        value="blinds-high-low"
                                        checked={filters.sortBy === 'blinds-high-low'}
                                        onChange={(e) => handleSortChange(e.target.value)}
                                    />
                                    <span>Blinds high to low</span>
                                </label>
                                <label className={`${styles['radio-label']} ${filters.sortBy === 'players-low-high' ? styles.selected : ''}`}>
                                    <input
                                        type="radio"
                                        name="sort"
                                        value="players-low-high"
                                        checked={filters.sortBy === 'players-low-high'}
                                        onChange={(e) => handleSortChange(e.target.value)}
                                    />
                                    <span>Players low to high</span>
                                </label>
                                <label className={`${styles['radio-label']} ${filters.sortBy === 'players-high-low' ? styles.selected : ''}`}>
                                    <input
                                        type="radio"
                                        name="sort"
                                        value="players-high-low"
                                        checked={filters.sortBy === 'players-high-low'}
                                        onChange={(e) => handleSortChange(e.target.value)}
                                    />
                                    <span>Players high to low</span>
                                </label>
                            </div>
                        </div>

                        {/* Stake Section */}
                        <div className={styles['stake-section']}>
                            <h3>Stakes:</h3>
                            <div className={styles['stake-options-row']}>
                                {['low', 'mid', 'high'].map((stake) => (
                                    <div
                                        key={stake}
                                        className={
                                            styles.stakeOptionDiv +
                                            (filters.stakes[stake] ? ' ' + styles.stakeOptionDivSelected : '')
                                        }
                                        onClick={() => handleStakeChange(stake, !filters.stakes[stake])}
                                    >
                                        {stake === 'low' && 'Low'}
                                        {stake === 'mid' && 'Mid'}
                                        {stake === 'high' && 'High'}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className={styles.buttons}>
                            <DrawerClose className={styles.clearFilters}>
                                <div
                                    variant="outline"
                                    size="sm"
                                    // className={styles.clearFilters}
                                    onClick={handleClearFilters}
                                >
                                    Reset Filters
                                </div>
                            </DrawerClose>
                            <DrawerClose onClick={handleApplyFilters} className={styles.applyFilters}>
                                <div
                                    variant="default"
                                    size="sm"
                                >
                                    Apply
                                </div>
                            </DrawerClose>
                        </div>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
};